import React, { createContext, useState, useEffect, useContext } from 'react';

export const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem('canaryCart');
    if (storedCart) {
      try {
        setCartItems(JSON.parse(storedCart));
      } catch (error) {
        console.error("Error parsing cart from localStorage:", error);
        localStorage.removeItem('canaryCart'); // Clear corrupted cart data
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('canaryCart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addItemToCart = (product, quantity, selectedVariant) => {
    setCartItems(prevItems => {
      // selectedVariant should be an object like { id, size, price } from productData.js
      // For products without variants (like Tote), selectedVariant.id would be product.variantId and selectedVariant.price would be product.price
      const itemIdentifier = selectedVariant ? selectedVariant.id : product.id; // Use variant ID if available, else product ID

      const existingItemIndex = prevItems.findIndex(
        item => (item.variantId ? item.variantId === itemIdentifier : item.id === itemIdentifier)
      );

      if (existingItemIndex > -1) {
        // Item already in cart, update quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += quantity;
        return updatedItems;
      } else {
        // Add new item to cart
        return [
          ...prevItems, 
          {
            ...product, // Spread all product details
            id: product.id, // Original product ID
            variantId: selectedVariant ? selectedVariant.id : product.variantId, // Specific variant ID for checkout
            name: product.name,
            price: selectedVariant ? selectedVariant.price : product.price, // Use variant price if applicable
            size: selectedVariant ? selectedVariant.size : null, // Store selected size
            image: product.category === 'Tee' && product.images ? product.images[0] : product.image, // Main image for cart display
            quantity
          }
        ];
      }
    });
  };

  const removeItemFromCart = (variantIdToRemove) => {
    setCartItems(prevItems => prevItems.filter(item => item.variantId !== variantIdToRemove));
  };

  const updateItemQuantity = (variantIdToUpdate, newQuantity) => {
    if (newQuantity <= 0) {
      removeItemFromCart(variantIdToUpdate);
    } else {
      setCartItems(prevItems => 
        prevItems.map(item => 
          item.variantId === variantIdToUpdate ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  // Function to generate Shopify checkout URL
  const generateShopifyCheckoutUrl = (storeDomain = "canarycanary.com") => {
    if (cartItems.length === 0) return null;
    const itemsQuery = cartItems.map(item => `${item.variantId}:${item.quantity}`).join(',');
    return `https://${storeDomain}/cart/${itemsQuery}`;
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addItemToCart,
      removeItemFromCart,
      updateItemQuantity,
      clearCart,
      getCartTotal,
      generateShopifyCheckoutUrl
    }}>
      {children}
    </CartContext.Provider>
  );
}; 